import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Table, Image, Modal, Space, Select } from 'antd';
import { PageHeader } from '@ant-design/pro-layout';
import { QRCodeCanvas } from 'qrcode.react';
import { jsPDF } from 'jspdf';
import { useSetRecoilState, useRecoilState } from 'recoil';
import { useMount } from 'react-use';
import {
  editBookingLocationAtom,
  selectedBookingLocationAtom,
  reloadBookingLocationAtom
} from '../../../../atoms/Atoms';
import EditLocations from './shared-components/EditLocations';
import bookingApi from '../../../../api/bookingApi';
import envConfig from '../../../../envConfig';

function Locations() {
  const { t } = useTranslation();
  const setSelectedLocation = useSetRecoilState(selectedBookingLocationAtom);
  const setEditLocation = useSetRecoilState(editBookingLocationAtom);
  const [reload, setReload] = useRecoilState(reloadBookingLocationAtom);
  const [loading, setLoading] = useState(false);
  const [locations, setLocations] = useState([]);
  const [qrLocation, setQrLocation] = useState(null);
  const [qrSize, setQrSize] = useState(1024);

  const qrCanvasId = 'booking-location-qr-canvas';
  const qrPreviewSize = 256;
  const qrSizeOptions = [256, 512, 1024, 2048, 4096];

  function getCheckinUrl(location) {
    return `${envConfig.BookingWebfrontUrl}/checkin/location/${location?.id}`;
  }

  function getQrCanvas() {
    return document.getElementById(qrCanvasId);
  }

  function saveQrAsImage() {
    const canvas = getQrCanvas();
    if (!canvas) {
      return;
    }
    const link = document.createElement('a');
    link.download = `location-${qrLocation?.id}-qr.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  }

  function saveQrAsPdf() {
    const canvas = getQrCanvas();
    if (!canvas) {
      return;
    }
    const imageData = canvas.toDataURL('image/png');
    const doc = new jsPDF();
    const size = 100;
    const x = (doc.internal.pageSize.getWidth() - size) / 2;
    doc.setFontSize(14);
    doc.text(qrLocation?.name ?? '', doc.internal.pageSize.getWidth() / 2, 25, {
      align: 'center',
    });
    doc.addImage(imageData, 'PNG', x, 35, size, size);
    doc.setFontSize(10);
    doc.text(
      getCheckinUrl(qrLocation),
      doc.internal.pageSize.getWidth() / 2,
      145,
      { align: 'center' }
    );
    doc.save(`location-${qrLocation?.id}-qr.pdf`);
  }

  function getLocations() {
    setLoading(true);
    bookingApi
      .get('locations')
      .then((response) => {
        setLoading(false);
        setLocations(response.data.data);
      })
      .catch((error) => {
        setLoading(false);
        console.error(error);
      });
  }

  useMount(() => {
    getLocations();
  });

  useEffect(() => {
    if (reload) {
      getLocations();
      setReload(false);
    }
  }, [reload]);

  return (
    <>
      <PageHeader
        className="mb-4 px-0"
        title={t('locations')}
        extra={[
          <Button
            id="reload"
            type="primary"
            key="reload"
            size="small"
            onClick={() => setReload(true)}
          >
            {t('reload')}
          </Button>,
          <Button
            type="primary"
            key="create"
            size="small"
            onClick={() => {
              setSelectedLocation({});
              setEditLocation(true);
            }}
          >
            {t('create_new')}
          </Button>
        ]}
      />
      <Table
        pagination={false}
        size="small"
        bordered
        rowKey="id"
        loading={loading}
        columns={[
          {
            title: t('image'),
            key: 'image',
            align: 'center',
            dataIndex: 'imageUrl',
            className: 'text-xs',
            render: (row) => (
              <>
                {row && (
                  <Image
                    alt="logo"
                    width={60}
                    src={row}
                    className="cursor-pointer"
                  />
                )}
              </>
            ),
          },
          {
            title: t('name'),
            key: 'name',
            align: 'center',
            dataIndex: 'name',
            className: 'text-xs',
            render: (row) => (
              <span>{row}</span>
            ),
          },
          {
            title: t('description'),
            key: 'description',
            align: 'center',
            dataIndex: 'description',
            className: 'text-xs',
            render: (row) => (
              <span>{row}</span>
            ),
          },
          {
            title: t('qr_code'),
            key: 'qr_code',
            align: 'center',
            className: 'text-xs',
            render: (text, record) => (
              <Button
                size="small"
                onClick={() => setQrLocation(record)}
              >
                {t('qr_code')}
              </Button>
            ),
          },
          {
            title: t('edit'),
            key: 'edit',
            align: 'center',
            className: 'text-xs',
            render: (text, record) => (
              <Button
                size="small"
                onClick={() => {
                  setSelectedLocation(record);
                  setEditLocation(true);
                }}
              >
                {t('edit')}
              </Button>
            ),
          },
        ]}
        dataSource={locations}
      />
      <Modal
        title={qrLocation?.name ?? t('qr_code')}
        open={!!qrLocation}
        onCancel={() => setQrLocation(null)}
        footer={[
          <Button key="close" onClick={() => setQrLocation(null)}>
            {t('close')}
          </Button>,
          <Button key="image" type="primary" onClick={saveQrAsImage}>
            {t('save_as_image')}
          </Button>,
          <Button key="pdf" type="primary" onClick={saveQrAsPdf}>
            {t('save_as_pdf')}
          </Button>,
        ]}
      >
        {qrLocation && (
          <Space direction="vertical" align="center" className="w-full">
            <Space align="center">
              <span className="text-xs">{t('resolution')}:</span>
              <Select
                size="small"
                value={qrSize}
                style={{ width: 130 }}
                onChange={(value) => setQrSize(value)}
                options={qrSizeOptions.map((size) => ({
                  value: size,
                  label: `${size} x ${size}`,
                }))}
              />
            </Space>
            <QRCodeCanvas
              id={qrCanvasId}
              value={getCheckinUrl(qrLocation)}
              size={qrSize}
              level="H"
              includeMargin
              style={{ width: qrPreviewSize, height: qrPreviewSize }}
            />
            <span className="text-xs break-all text-center">
              {getCheckinUrl(qrLocation)}
            </span>
          </Space>
        )}
      </Modal>
      <EditLocations />
    </>
  );
}
export default Locations;
